import {Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {Tile, TILE_TYPE_SIMPLE_ANALOG, TILE_TYPE_SIMPLE_BOOL, TILE_TYPE_SIMPLE_MULTISTATE} from '../../../shared/models/tile';
import {
  Schedule,
  ScheduleConfig,
  ScheduleDayOfWeek,
  ScheduleHourOfDay,
  ScheduleMinuteOfHour,
} from '../../../shared/models/schedule';
import {DayPeriod, TimeConverter} from '../../../shared/models/time';
import {OptionGroup, OptionId} from '../../../shared/models/option';
import {ShadeToolsService} from '../../../core/services/shade-tools.service';
import {DisplayState} from '../../../shared/models/display-state';
import {
  SimpleAnalogStructure,
  SimpleBoolStructure,
  SimpleMultistateStructure,
} from '../../../shared/models/structure';
import {ConnectedPosition} from '@angular/cdk/overlay';
import {SchedulesService} from '../../../core/services/schedules.service';
import {DrawerGapFromTopComponent} from '../drawer-gap-from-top/drawer-gap-from-top.component';
import {FormsModule} from '@angular/forms';
import {PopoverMenuComponent} from '../popover-menu/popover-menu.component';
import {SpinnerComponent} from '../spinner/spinner.component';
import {TimePickerComponent} from '../time-picker/time-picker.component';

@Component({
  selector: 'motn-schedule-editor',
  templateUrl: './schedule-editor.component.html',
  styleUrls: ['./schedule-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    DrawerGapFromTopComponent,
    FormsModule,
    PopoverMenuComponent,
    SpinnerComponent,
    TimePickerComponent,
  ],
})
export class ScheduleEditorComponent implements OnInit {
  @Input() tile: Tile;
  @Input() schedule: Schedule;
  @Input() allSchedules: Schedule[]; // for validating we're not conflicting with another schedule for same tile

  @Output() canceled$: EventEmitter<any> = new EventEmitter<any>();
  @Output() saved$: EventEmitter<Schedule> = new EventEmitter<Schedule>();
  @Output() deleted$: EventEmitter<string> = new EventEmitter<string>();

  expiration: string = '';

  INFO_TIME_ZONE = `The time chosen will be treated relative to the time zone of the hub that controls this tile. If the hub's time zone is changed, the time of day chosen here will not be automatically adjusted; it will then be followed in the hub's new time zone.\n\nFor example, if the hub is currently set to US/Eastern and the time chosen above is set to 9:30am, then the operation will run at 9:30am in US/Eastern time. If the hub's time zone then changes to US/Pacific, the operation will then run at 9:30am in US/Pacific time.`;
  INFO_EXPIRATION = `If set, the schedule will never run again after the chosen datetime has passed.\n\nThe datetime entered will be the very last possible time the schedule will be permitted to run. After this datetime has passed, the schedule will be automatically deleted within a few hours.`;

  SIMPLE_ANALOG = TILE_TYPE_SIMPLE_ANALOG;
  SIMPLE_BOOL = TILE_TYPE_SIMPLE_BOOL;
  SIMPLE_MULTISTATE = TILE_TYPE_SIMPLE_MULTISTATE;

  daysOfWeek: string[] = [
    'Sun',
    'Mon',
    'Tue',
    'Wed',
    'Thu',
    'Fri',
    'Sat',
  ];

  readonly overlayPositions: ConnectedPosition[] = [{
    originX: 'center',
    originY: 'center',
    overlayX: 'center',
    overlayY: 'center',
  }];

  deleting: boolean = false;
  saving: boolean = false;

  selectedEnabled: 0 | 1 = 0;
  selectedValue: boolean | number | string;
  selectedHour: number = 12;
  selectedMinute: number = 0;
  selectedPeriod: DayPeriod = DayPeriod.AM;
  selectedDaysOfWeek: boolean[] = [
    false, false, false, false, false, false, false,
  ];

  valueOptions: OptionGroup;
  valueOptionsShown: boolean = false;


  constructor(
    private schedulesService: SchedulesService,
    private shadeTools: ShadeToolsService,
  ) {}


  ngOnInit(): void {
    this.schedule = JSON.parse(JSON.stringify(this.schedule));

    switch (this.tile.Type) {
      case this.SIMPLE_BOOL:
        let bStructure = this.tile.Structure as SimpleBoolStructure;

        this.valueOptions = new OptionGroup([{
          id: '0',
          name: bStructure.DisplayFalse,
        }, {
          id: '1',
          name: bStructure.DisplayTrue,
        }]);

        break;
      case this.SIMPLE_MULTISTATE:
        let mStructure = this.tile.Structure as SimpleMultistateStructure;

        let ds: DisplayState[] = mStructure.DisplayStates;

        this.valueOptions = new OptionGroup(ds.map((s) => {
          return {
            id: '' + s.CompareFunc.Value,
            name: s.DisplayText,
          };
        }));

        break;
    }

    if (!this.schedule.UUID) {
      this.selectedEnabled = 1;
      this.selectedPeriod = DayPeriod.PM;
      this.selectedHour = 12;
      this.selectedMinute = 30;
      this.selectedDaysOfWeek = [
        false, true, true, true, true, true, false,
      ];
    } else {
      this.selectedEnabled = this.schedule.Config.Actions[0].SetValue.Enabled ? 1 : 0;
      this.selectedValue = this.schedule.Config.Actions[0].SetValue.Value;

      const time12 = TimeConverter.from24Hour(this.schedule.Config.Hour, this.schedule.Config.Minute, 0);
      this.selectedHour = time12.hour;
      this.selectedMinute = time12.minute;
      this.selectedPeriod = time12.period;

      this.schedule.Config.DaysOfWeek.map((dow) => {
        this.selectedDaysOfWeek[dow] = true;
      });
    }

  }


  getValueOptionText(optionId: OptionId): string {
    return this.valueOptions.options.find(o => o.id === optionId)?.name;
  }


  getAnalogStructure(): SimpleAnalogStructure {
    return this.tile.Structure as SimpleAnalogStructure;
  }


  onChangeExpiration() {
    // Expiration changed
  }


  infoDialog(text: string): void {
    this.shadeTools.infoDialog(text);
  }


  /**
   * Checks to see if there is any other schedule for the same tile which conflicts
   * with this schedule's hour/minute and days settings
   *
   * @private
   */
  private conflictExists(): boolean {
    this.updateScheduleConfig();

    // schedules whose minute/hour pair are the same as this.schedule's
    const potentialConflicts = this.allSchedules.filter((s) => {
      return (
        s.Config.Hour === this.schedule.Config.Hour
        && s.Config.Minute === this.schedule.Config.Minute
        && s.UUID !== this.schedule.UUID
      );
    });

    for (let pot of potentialConflicts) {
      for (let dow of pot.Config.DaysOfWeek) {
        if (this.schedule.Config.DaysOfWeek.includes(dow)) {
          return true;
        }
      }
    }

    return false;
  }


  /**
   * Interprets the values in the various this.selected_____ properties and stores
   * them in proper form into this.schedule so that a conflict check or emission of
   * this.schedule can take place.
   *
   * @private
   */
  private updateScheduleConfig() {
    const c: ScheduleConfig = this.schedule.Config;

    c.Actions[0].SetValue.Value = this.selectedValue;
    c.Actions[0].SetValue.Enabled = !!this.selectedEnabled;

    const time24 = TimeConverter.to24Hour({
      hour: this.selectedHour,
      minute: this.selectedMinute,
      second: 0,
      period: this.selectedPeriod,
    });

    c.Hour = time24.hour as ScheduleHourOfDay;
    c.Minute = time24.minute as ScheduleMinuteOfHour;

    c.DaysOfWeek = [];

    this.selectedDaysOfWeek.map((on, i: number) => {
      if (on) {
        c.DaysOfWeek.push(i as ScheduleDayOfWeek);
      }
    });
  }


  save() {
    this.updateScheduleConfig();

    if (!this.validate()) {
      return;
    }

    if (this.conflictExists()) {
      this.shadeTools.simpleOkDialog('Conflict', `Cannot save because another schedule for the same tile runs at the same time of day at least once per week as this schedule is configured to run.\n\nTo resolve the conflict, ensure there are no time+day overlaps with other schedules for this tile.`);
      return;
    }

    this.saving = true;

    const scheduleIsNew: boolean = !this.schedule.UUID;

    if (scheduleIsNew) {
      this.schedulesService.createSchedule$(this.schedule).subscribe(
        (s) => {
          this.schedule = s;
          this.saved$.emit(s);
        },
        () => {
          this.shadeTools.errorDialog('Something went wrong while creating the new schedule. Please try again.');
          this.saving = false;
        },
      );
    } else {
      this.schedulesService.updateSchedule$(this.schedule).subscribe(
        (s) => {
          this.schedule = s;
          this.saved$.emit(s);
        },
        () => {
          this.shadeTools.errorDialog('Something went wrong while attempting to save changes to the schedule. Please try again.');
          this.saving = false;
        },
      );
    }
  }


  validate(): boolean {
    if (this.schedule.Config.DaysOfWeek.length === 0 && this.schedule.Config.Actions[0].SetValue.Enabled) {
      this.shadeTools.simpleOkDialog('Missing Days of Week', `This schedule cannot be saved because no days of the week are selected. Select at least one day of the week on which the schedule should run.`);
      return false;
    }

    if (typeof this.schedule.Config.Actions[0].SetValue.Value === 'undefined' || this.schedule.Config.Actions[0].SetValue.Value === null) {
      this.shadeTools.simpleOkDialog('Missing Value', `This schedule cannot be saved because the value is empty.`);
      return false;
    }

    return true;
  }


  cancel() {
    this.canceled$.emit(1);
  }


  delete() {
    this.deleting = true;

    this.schedulesService.deleteSchedule$(this.schedule.UUID).subscribe(
      (_) => {
        this.deleted$.emit(this.schedule.UUID);
        this.deleting = false;
      },
      () => {
        this.shadeTools.errorDialog();
        this.deleting = false;
      },
    );
  }
}
